import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthQuery } from 'app/core/auth/auth.query';
import { agrees, User } from 'app/core/auth/user.model';
import { changeBaseMap, setView } from 'app/core/map/map.actions';
import { Basemap } from 'app/shared/ll/models/basemap';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, skipWhile, take } from 'rxjs/operators';
import { Feature, isLayerDef, LayerItemDef } from './layer.model';
import {
    clearLayers,
    mapLayersNamesToggle,
    redoSearch,
    selectLayer,
    toggleLayerUpdate,
    toggleShowParcels,
    unselectLayer
} from './layers.actions';
import { getAvailableLayers, getFeatures, getLayersDef, getSelectedLayers, getShowParcels } from './layers.reducer';


export interface Layers {
    [layerKey: string]: {
        [featureId: string]: Feature;
    };
}

export const userAgreesWithRentalPolicy = (user: User) => agrees(user, 'rental_policy');


@Injectable({
    providedIn: 'root',
})
export class LayersService {

    readonly layersDef = combineLatest([this.store.select(getLayersDef), this.authQuery.getUserInfo$()])
        .pipe(
            map(([layersDef, userInfo]) => layersDef?.map(l => isLayerDef(l) && l.key === 'rental_policy'
                ? {
                    ...l,
                    optionsComponent: userAgreesWithRentalPolicy(userInfo) ? null : l.optionsComponent,
                }
                : l
            )),
            filter(layersDef => !!layersDef)) as Observable<LayerItemDef[]>;

    availableLayers = this.store.select(getAvailableLayers);

    selectedLayers =
        this.store.select(getSelectedLayers).pipe(map(selection => Object.entries(selection).map(([k, v]) => ({ ...v, key: k }))));

    constructor(private authQuery: AuthQuery, private store: Store) {}

    getLayers(): Observable<Layers> {
        return this.store.select(getFeatures).pipe(
            map(features => Object.keys(features).reduce(
                (acc, key) => {
                    // @ts-ignore
                    acc[key] = features[key].reduce((a, i) => { a[i.id] = i; return a; }, {});
                    return acc;
                },
                {}
            ))
        );
    }

    getShowParcels() {
        return this.store.select(getShowParcels);
    }

    getFeatures() {
        return this.store.select(getFeatures).pipe(
            map(features => Object.keys(features).reduce((acc, key) => acc.concat(features[key]), [] as Feature[]))
        );
    }

    selectLayer(key: string, args?: any, updating = true) {
        this.store.dispatch(selectLayer({ key, args, updating }));
    }

    zoomMapAndSelectLayer(zoom: number, key: string, args?: any, updating = true) {
        this.store.dispatch(setView({ zoom }));
        this.availableLayers
            .pipe(
                skipWhile(layers => !(layers.find(l => l.layerKey === key) || { zoomSatisfied: true }).zoomSatisfied),
                take(1)
            )
            .subscribe(_ => {
                this.selectLayer(key, args, updating);
            });
    }

    redoSearch(key: string) {
        this.store.dispatch(redoSearch({ key }));
    }

    toggleUpdate(key: string, updating: boolean) {
        this.store.dispatch(toggleLayerUpdate({ key, updating }));
    }

    deselectLayer(key: string) {
        this.store.dispatch(unselectLayer({ key }));
    }

    clearLayers() {
        this.store.dispatch(clearLayers());
    }

    toggleLayerMapNames(value: boolean) {
        this.store.dispatch(mapLayersNamesToggle({ mapLayerNames: value }));
    }

    toggleShowParcels(value: boolean) {
        this.store.dispatch(toggleShowParcels({ value }));
    }

    selectBasemap(value: Basemap) {
        this.store.dispatch(changeBaseMap({ baseMap: value.key }));
    }
}
